import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'index'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { handleErrors } from 'utils/handle-errors'

interface BlockedNumber {
  id: string
  phone_number: string
  block_type: 'calls' | 'texts' | 'both'
  reason?: string
  created_at: string
  updated_at: string
  created_by_email?: string
  created_by_type?: string
}

interface BlockedAttempt {
  id: string | number
  from_number: string
  to_number: string
  attempt_type: 'call' | 'text'
  block_reason: string
  message_preview?: string | null
  created_at: string
  from_phone?: string
  to_phone?: string
  blocked_number?: {
    id: number
    block_type: string
    reason?: string
  }
}

interface BlockedAttemptsStats {
  total_blocked: number
  blocked_today: number
  blocked_calls: number
  blocked_texts: number
  by_reason?: Record<string, number>
}

export const useGetUserBlockedNumbers = (userId?: string) => {
  return useQuery(
    ['user-blocked-numbers', userId],
    async () => {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_numbers`,
        {
          method: API_METHODS.GET,
          headers: apiHeaders(store.getState()),
        }
      )
      handleErrors(response)
      const json = await response.json()
      return json as { blocked_numbers: BlockedNumber[] }
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    }
  )
}

export const useCreateUserBlockedNumber = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: {
      phone_number: string
      block_type: string
      reason?: string
    }) => {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_numbers`,
        {
          method: API_METHODS.POST,
          headers: apiHeaders(store.getState()),
          body: JSON.stringify({ blocked_number: data }),
        }
      )
      handleErrors(response)
      const json = await response.json()
      return json
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-blocked-numbers', userId])
      },
    }
  )
}

export const useUpdateUserBlockedNumber = (
  userId: string,
  blockedNumberId: string
) => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: { block_type?: string; reason?: string }) => {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_numbers/${blockedNumberId}`,
        {
          method: API_METHODS.PUT,
          headers: apiHeaders(store.getState()),
          body: JSON.stringify({ blocked_number: data }),
        }
      )
      handleErrors(response)
      const json = await response.json()
      return json
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-blocked-numbers', userId])
      },
    }
  )
}

export const useDeleteUserBlockedNumber = (
  userId: string,
  blockedNumberId: string
) => {
  const queryClient = useQueryClient()

  return useMutation(
    async () => {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_numbers/${blockedNumberId}`,
        {
          method: API_METHODS.DELETE,
          headers: apiHeaders(store.getState()),
        }
      )
      handleErrors(response)
      const json = await response.json()
      return json
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-blocked-numbers', userId])
      },
    }
  )
}

export const useGetUserBlockedAttempts = (
  userId?: string,
  params?: {
    start_date?: string
    end_date?: string
    attempt_type?: string
    block_reason?: string
    page?: number
    per_page?: number
  }
) => {
  return useQuery(
    ['user-blocked-attempts', userId, params],
    async () => {
      const queryParams = new URLSearchParams()
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, String(value))
          }
        })
      }
      const url = `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_attempts${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`

      const response = await fetch(url, {
        method: API_METHODS.GET,
        headers: apiHeaders(store.getState()),
      })
      handleErrors(response)
      const json = await response.json()
      return json as {
        blocked_attempts: BlockedAttempt[]
        meta: {
          current_page: number
          total_pages: number
          total_count: number
          per_page: number
        }
      }
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    }
  )
}

export const useGetUserBlockedAttemptsStats = (userId?: string) => {
  return useQuery(
    ['user-blocked-attempts-stats', userId],
    async () => {
      const response = await fetch(
        `${BASE_API_URL}/admin/api/v2/users/${userId}/blocked_attempts/stats`,
        {
          method: API_METHODS.GET,
          headers: apiHeaders(store.getState()),
        }
      )
      handleErrors(response)
      const json = await response.json()
      // Handle both direct stats or nested in a stats object
      return (json?.stats || json) as BlockedAttemptsStats
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    }
  )
}
